import LogoutIcon from '@mui/icons-material/Logout';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import type { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ROLE_LABELS } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';

import { accountEntries } from '../navigation';

import { useLogOut } from './useLogOut';

/** Initials for the avatar, so it says something before a photo exists. */
function initials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * The account rail from the wireframes - Profile, Account, Users, Billing,
 * Activity, Log Out - as a menu behind the avatar.
 *
 * It is a menu rather than a fixed column because a second fixed column does
 * not fit below large screens, and MUI's Menu already handles focus, Escape and
 * the keyboard.
 */
export function AccountMenu() {
  const { user } = useAuth();
  const logOut = useLogOut();
  // From the width where the rail is on screen, the menu does not repeat its
  // entries: it keeps who is signed in, and Log Out.
  const railShown = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  // While viewing, staff get the organization's account entries, which the
  // route guard lets them open read-only.
  const { viewing } = useViewingAs();
  const entries = railShown ? [] : accountEntries(user?.role ?? null, Boolean(viewing));
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  return (
    <>
      <Tooltip title="Account">
        <IconButton
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label="Account"
          aria-haspopup="menu"
          aria-expanded={open}
          size="small"
        >
          <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
            {user ? initials(user.name) : '?'}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        slotProps={{ list: { 'aria-label': 'Account' } }}
      >
        {/* Who is signed in. The menu held nothing but Log Out for an Admin,
            preceded by a divider dividing nothing, and never said whose account
            it was - which matters most on a shared machine. Not a menu item: it
            is a label, and making it focusable would put a dead stop in the
            keyboard order. */}
        <Box sx={{ px: 2, py: 1, maxWidth: 260 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {user?.name ?? 'Signed in'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="p">
            {user?.email}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="p">
            {user ? ROLE_LABELS[user.role] : ''}
          </Typography>
        </Box>

        <Divider />

        {/* Entries this role may not use are left out rather than shown and
            then refused. Hiding is the courtesy; the route guard is what
            actually stops a typed address. */}
        {entries.map((item) => (
          <MenuItem
            key={item.path}
            component={RouterLink}
            to={item.path}
            onClick={() => setAnchor(null)}
          >
            {item.label}
          </MenuItem>
        ))}

        {entries.length ? <Divider /> : null}

        <MenuItem
          onClick={() => {
            setAnchor(null);
            logOut();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Log Out
        </MenuItem>
      </Menu>
    </>
  );
}
